import {
  createAsyncThunk,
  createSlice,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  InvoiceFilters,
  InvoicesFetch,
  getInvoices,
  getEezyCompanies,
  EezyCompany,
  EezyCompanyFilters,
  updateInvoiceWave,
  InvoiceWavePost,
  InvoiceEvent,
  InvoiceDisplay,
  sendInvoicesWithIds,
  CreditInvoiceRequest,
  createNewCreditInvoice,
} from "./invoiceAPI";
import { messageActions, NotificationLink } from "../message/messageSlice";
import isEmpty from "../../common/helpers/isEmpty";
import { translate } from "../messaging/helpers";
import { routes } from "../../app/paths";

export type SliceState = {
  ids: [];
  entities: {};
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
};

const invoiceAdapter = createEntityAdapter<InvoiceDisplay>({
  sortComparer: (a, b) =>
    (a.invoiceDate || "").localeCompare(b.invoiceDate || "") ||
    Number(b.invoiceNumber) - Number(a.invoiceNumber),
});

export const fetchInvoices = createAsyncThunk<
  InvoicesFetch,
  InvoiceFilters,
  { state: RootState }
>("invoices/fetchInvoices", async (filters: InvoiceFilters, { requestId }) => {
  const response = await getInvoices(filters, {
    requestId,
  });
  return response.data;
});

export const fetchEezyCompanies = createAsyncThunk<
  EezyCompany[],
  EezyCompanyFilters,
  { state: RootState }
>("invoices/fetchEezyCompanies", async (filters: {}, { requestId }) => {
  const response = await getEezyCompanies(filters, {
    requestId,
  });
  return response;
});

export const changeInvoiceWave = createAsyncThunk<
  InvoiceEvent[],
  InvoiceWavePost,
  { state: RootState }
>(
  "invoices/changeInvoiceWave",
  async (invoiceWave: InvoiceWavePost, { requestId }) => {
    const response = await updateInvoiceWave(invoiceWave, { requestId });
    return response.data;
  },
);

export const sendInvoices = createAsyncThunk<
  InvoiceEvent[],
  string[],
  { state: RootState }
>(
  "invoices/sendInvoices",
  async (invoiceIds: string[], { requestId, dispatch }) => {
    const response = await sendInvoicesWithIds(invoiceIds, { requestId });
    let hasChanges: string[] = [];
    let hasBeenDeleted: string[] = [];
    response.data.forEach((event: InvoiceEvent) => {
      if (event.name === "invChg") {
        hasChanges.push(event.id);
      }
      if (event.name === "invDel") {
        hasBeenDeleted.push(event.id);
      }
    });
    if (!isEmpty(hasChanges) || !isEmpty(hasBeenDeleted)) {
      const changesText = isEmpty(hasChanges)
        ? ""
        : hasChanges.length > 1
          ? `${translate("invoices:invoicesNotSent")} ${hasChanges.map((id) => `__${id}__`).join(" ")}`
          : `${translate("invoices:invoiceNotSent")} __${hasChanges[0]}__`;
      const links = hasChanges.reduce(
        (acc, id) => {
          acc[id] = {
            text: id.toString(),
            href: `${routes.invoicePreview(id)}`,
          };
          return acc;
        },
        {} as Record<string, NotificationLink>,
      );
      const deletedText = isEmpty(hasBeenDeleted)
        ? ""
        : hasBeenDeleted.length > 1
          ? `${translate("invoices:invoicesDeleted")} ${"\r\n"} ${hasBeenDeleted.map((id) => `${id}`).join("\r\n")}`
          : `${translate("invoices:invoiceDeleted")} ${"\r\n"} ${hasBeenDeleted[0]}`;
      dispatch(
        messageActions.setNotification({
          title: translate("invoices:expensesChangedTitle"),
          text:
            changesText + `${!isEmpty(hasChanges) ? "\r\n" : ""}${deletedText}`,
          links,
        }),
      );
    }
    if (isEmpty(hasChanges) && !isEmpty(response.data)) {
      dispatch(
        messageActions.setMessage({
          type: "success",
          text:
            invoiceIds.length > 1
              ? translate("invoices:invoicesSent")
              : translate("invoices:invoiceSent"),
        }),
      );
    }
    return response.data;
  },
);

export const createCreditInvoice = createAsyncThunk<
  InvoiceEvent[],
  CreditInvoiceRequest
>(
  "invoicesDetailed/createCreditInvoice",
  async (invoiceData: CreditInvoiceRequest, { requestId }) => {
    const response = await createNewCreditInvoice(invoiceData, { requestId });
    return response.data;
  },
);

export const invoiceSlice = createSlice({
  name: "invoices",
  initialState: invoiceAdapter.getInitialState({
    status: "idle",
  }) as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        invoiceAdapter.setAll(state, action.payload.data);
        state.status = "succeeded";
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(changeInvoiceWave.pending, (state) => {
        state.status = "pending";
      })
      .addCase(changeInvoiceWave.fulfilled, (state, action) => {
        action.payload.forEach(({ id, payload }) => {
          invoiceAdapter.updateOne(state, {
            id,
            changes: { invoiceWave: payload.invoiceWave },
          });
        });
        state.status = "succeeded";
      })
      .addCase(changeInvoiceWave.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(sendInvoices.pending, (state) => {
        state.status = "pending";
      })
      .addCase(sendInvoices.fulfilled, (state, action) => {
        const changes: { id: string; changes: Partial<InvoiceDisplay> }[] =
          action.payload.map((updatedEvent) => {
            switch (updatedEvent.name) {
              case "invSntPnd":
                return {
                  id: updatedEvent.id,
                  changes: {
                    invoiceStatus: "SentPending",
                    version: updatedEvent.version,
                  },
                };
              case "invChg":
                return {
                  id: updatedEvent.id,
                  changes: {
                    invoiceDueDate: updatedEvent.payload.invoiceDueDate,
                    invoiceStatus: updatedEvent.payload.invoiceStatus,
                    version: updatedEvent.version,
                  },
                };
              default:
                return {
                  id: updatedEvent.id,
                  changes: {
                    version: updatedEvent.version,
                  },
                };
            }
          });
        invoiceAdapter.updateMany(state, changes);
        state.status = "succeeded";
      })
      .addCase(sendInvoices.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(createCreditInvoice.fulfilled, (state, action) => {
        // Go through invoice events and update the invoices with the latest data
        action.payload.forEach((event) => {
          const { id, version, payload } = event;

          const getBuyerCostCenterIdAndName = () => {
            if (payload.invoiceRows?.length > 0) {
              const firstItem = payload.invoiceRows[0].customerCostCenter;
              if (firstItem?.id) {
                const invoiceRowsHaveSameCCs = payload.invoiceRows.every(
                  (row: any) => row.customerCostCenter?.id === firstItem.id,
                );
                if (invoiceRowsHaveSameCCs) {
                  // If all invoice rows have the same cost center
                  return {
                    buyerCostCenterId: firstItem.id,
                    buyerCostCenterName: firstItem?.name || "",
                  };
                }
              }
            }
            // If no rows or different cost centers
            return {
              buyerCostCenterId: null,
              buyerCostCenterName: "",
            };
          };

          switch (event.name) {
            case "invNew":
              // Format the invoice object
              const newInvoice = {
                id: id,
                version: version,
                invoiceNumber: payload.invoiceNumber,
                originalInvoiceNumber: payload.originalInvoiceNumber,
                bundleId: payload.bundleId,
                invoiceType: payload.invoiceType,
                originCode: payload.originCode,
                eezyCompanyId: payload.eezyCompanyId,
                eezyCompanyName: payload.eezyCompanyName,
                buyerId: payload.customerOrganizationId,
                buyerName: payload.customerOrganizationName,
                buyerCostCenterCode: null,
                ...getBuyerCostCenterIdAndName(),
                invoiceDate: payload.invoiceDate,
                invoiceDueDate: payload.invoiceDueDate,
                invoiceValueDate: payload.invoiceValueDate,
                schedulingPeriodStart: payload.schedulingPeriodStart,
                schedulingPeriodEnd: payload.schedulingPeriodEnd,
                salesPersonName: payload.salesPersonName,
                invoiceStatus: payload.invoiceStatus,
                invoiceWave: payload.invoiceWave,
                vatExcludedAmount: payload.vatExcludedAmount,
                vatAmount: payload.vatAmount,
                vatIncludedAmount: payload.vatIncludedAmount,
              };
              invoiceAdapter.addOne(state, newInvoice);
              break;
            case "invChg":
            case "invBndlIdChg":
              // Update version and bundleId
              const changes = {
                version: version,
                bundleId: payload.bundleId,
              };
              invoiceAdapter.updateOne(state, {
                id: event.id,
                changes: changes,
              });
              break;
            default:
              break;
          }
        });
      });
  },
});

export const { selectAll, selectById, selectIds } = invoiceAdapter.getSelectors(
  (state: RootState) => state.invoices,
);

export default invoiceSlice.reducer;
